import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { BLACK_12_OPACITY } from "../../utils/colors";

/**
 * CircularLoader
 * - Progress increases based on estimated time, slowing down as it approaches max.
 * - Never reaches 100% on its own (maxProgress = 99).
 * - When progress prop is provided externally, it uses that value directly.
 */
const CircularLoader = ({
  size = 64,
  thickness = 4,
  color = "primary",
  trackColor = BLACK_12_OPACITY,
  time = 45000,
  progress: progressValue,
  autoIncrement = true,
  onProgressChange,
  sx = {},
  containerSx = {},
}) => {
  const [progress, setProgress] = useState(progressValue || 0);

  const hasProgressValue = typeof progressValue === "number";

  // Auto-increment progress based on time with deceleration
  useEffect(() => {
    if (hasProgressValue && !autoIncrement) return;

    const maxProgress = 99;
    const baseIncrement = 100 / (time / 1000);

    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= maxProgress) {
          return prevProgress;
        }

        // Slow down the increment as it approaches 100%
        // Using square root to keep factor higher at the beginning (faster start)
        const slowDownFactor = Math.sqrt((100 - prevProgress) / 100);
        const increment = baseIncrement * slowDownFactor;

        return Math.min(prevProgress + increment, maxProgress);
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [time, hasProgressValue, autoIncrement]);

  // Sync with external progress value
  useEffect(() => {
    if (hasProgressValue) {
      setProgress(progressValue);
    }
  }, [progressValue, hasProgressValue]);

  // Notify parent of progress changes
  useEffect(() => {
    if (onProgressChange) {
      onProgressChange(progress);
    }
  }, [progress, onProgressChange]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...containerSx,
      }}
    >
      {/* Track (background) */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ color: trackColor, position: "absolute", left: 0, top: 0 }}
      />

      {/* Progress */}
      <CircularProgress
        variant="determinate"
        value={progress}
        size={size}
        thickness={thickness}
        color={color}
        sx={sx}
      />
    </Box>
  );
};

export default CircularLoader;
